import React, { ComponentProps, FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { FadeInElement } from '@components/animations/FadeInElement/FadeInElement.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { CharCounter } from '@components/CharCounter/CharCounter.component';
import { Text } from '@components/Text/Text.component';
import { Validator } from '@form/business/form.types';
import { FormHints } from '@form/components/FormHints/FormHints.component';
import { Textarea } from '@form/components/Textarea/Textarea.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { RootState } from '@store/store';
import classnames from 'classnames';
import styles from './CommentsTextArea.module.scss';

const MAX_CHAR_INPUT = 1000;

interface Props extends Omit<ComponentProps<typeof Textarea>, 'onChange' | 'isHintVisible'> {
  isHintVisible?: Validator['type'];
  onCommentSubmit: (comment: string) => void;
  onChange?: (value: string) => void;
}

const CommentsTextarea: FC<Props> = ({ isHintVisible, onCommentSubmit, disabled, className, onChange, ...rest }) => {
  const [inputString, setInputString] = useState<string | undefined>(undefined);
  const { t } = useTranslation(['actu']);
  const { user } = useSelector((state: RootState) => state.user);

  const onInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value } = event.target;
    setInputString(value);
    if (onChange) {
      onChange(value);
    }
  };

  useEffect(() => {
    if (isHintVisible === 'success') {
      setInputString('');
    }
  }, [isHintVisible, setInputString]);

  const validators: Validator[] = [
    {
      isValid: isHintVisible !== 'success',
      message: t('actu.comments.send-comment.success'),
      type: 'success',
    },
    {
      isValid: isHintVisible !== 'error',
      message: t('actu.comments.send-comment.error'),
      type: 'error',
    },
  ];
  function onSubmitClick() {
    if (inputString) onCommentSubmit(inputString);
  }

  const rootClass = classnames(styles['comments-textarea'], className);
  return (
    <>
      <div className={rootClass}>
        <Textarea
          autosize
          label={
            <div className="flex space-between">
              {user && (
                <Text className="plc-mb-s" tag="p" variant="h4">
                  {user.username}
                </Text>
              )}
              <CharCounter inputString={inputString} maxChar={MAX_CHAR_INPUT} textProps={{ variant: 'tiny-title' }} />
            </div>
          }
          labelInsideBorder
          maxLength={MAX_CHAR_INPUT}
          onChange={onInputChange}
          placeholder={t('actu.comments.add-comment-placeholder')}
          title={user && user.username}
          value={inputString}
          {...rest}
        />

        <FadeInElement className="plc-mt-m" show={Boolean(inputString && inputString.length > 0)}>
          <PrimaryButton
            className="plc-mb-m"
            isDarkGreen
            isSmall
            onClick={onSubmitClick}
            textProps={{ i18nKey: 'actu.comments.send-comment' }}
            type="submit"
          />
        </FadeInElement>
      </div>
      <FormHints isHintVisible={isHintVisible && !disabled} validators={validators} />
    </>
  );
};

export { CommentsTextarea };
